/**
 * Admin Educator Insights Page
 * Anonymous aggregated data from all educator dashboards.
 * Accessible only to admins via /admin/educator-insights
 */

import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { GraduationCap, Download, BarChart3, School, TrendingUp } from "lucide-react";
import { toast } from "sonner";

interface EducatorInsights {
  activeEducators: number;
  classBreakdown: { grade_level: string; school_name: string; class_count: number }[];
  domainAverages: Record<string, number> & { total_checkins?: number };
  domainTrends: { month: string; checkin_count: number; [key: string]: any }[];
}

const DOMAIN_LABELS: Record<string, string> = {
  emotional_awareness: "Colorful Chameleon",
  self_regulation: "Steady Oak",
  courage: "Brave Thing",
  kindness: "Warm Hearted",
  gratitude: "Grateful Heart",
  sharing_repair: "Bridge Builder",
  mattering: "You Matter",
};

export default function AdminEducatorInsights() {
  const [insights, setInsights] = useState<EducatorInsights | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInsights();
  }, []);

  const loadInsights = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) return;
      const res = await fetch("/api/educator/admin/insights", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setInsights(data);
      } else {
        toast.error("Failed to load educator insights");
      }
    } catch (error) {
      console.error("Educator insights error:", error);
      toast.error("Failed to load educator insights");
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!insights) return;
    const rows = [
      ["Dira Educator Insights Export", new Date().toLocaleDateString()],
      [],
      ["Active Educators", insights.activeEducators],
      ["Total Check-ins", insights.domainAverages.total_checkins || 0],
      [],
      ["Domain Averages (1-3 scale)"],
      ["Domain", "Average Rating"],
      ...Object.entries(DOMAIN_LABELS).map(([key, label]) => [
        label, insights.domainAverages[key] || "—"
      ]),
      [],
      ["School & Grade Breakdown"],
      ["School", "Grade Level", "Classes"],
      ...insights.classBreakdown.map(r => [r.school_name || "—", r.grade_level || "—", r.class_count]),
      [],
      ["Domain Trends Over Time"],
      ["Month", ...Object.values(DOMAIN_LABELS), "Check-ins"],
      ...insights.domainTrends.map(r => [
        new Date(r.month).toLocaleDateString("en-US", { month: "short", year: "numeric" }),
        ...Object.keys(DOMAIN_LABELS).map(k => r[k] || "—"),
        r.checkin_count,
      ]),
    ];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dira-educator-insights-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-200 rounded animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-forest-600" />
            Educator Insights
          </h1>
          <p className="text-gray-500 mt-0.5">Anonymous aggregated data from all educator dashboards</p>
        </div>
        <Button
          onClick={exportCSV}
          disabled={!insights}
          variant="outline"
          className="flex items-center gap-2 border-forest-200 text-forest-700"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </Button>
      </div>

      {!insights ? (
        <Card>
          <CardContent className="py-16 text-center">
            <GraduationCap className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No educator data available yet</p>
            <p className="text-gray-400 text-sm mt-1">Data will appear once educators start using their dashboards</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Top stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-forest-50 rounded-lg border border-forest-200">
              <div className="text-3xl font-bold text-forest-700">{insights.activeEducators}</div>
              <div className="text-sm text-forest-600 mt-1">Active Educators</div>
            </div>
            <div className="text-center p-4 bg-golden-50 rounded-lg border border-golden-200">
              <div className="text-3xl font-bold text-golden-700">{insights.domainAverages.total_checkins || 0}</div>
              <div className="text-sm text-golden-600 mt-1">Total Check-ins</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-3xl font-bold text-blue-700">{insights.classBreakdown.length}</div>
              <div className="text-sm text-blue-600 mt-1">Class Groups</div>
            </div>
          </div>

          {/* Domain averages */}
          {(insights.domainAverages.total_checkins || 0) > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <BarChart3 className="w-4 h-4" /> Domain Averages
                </CardTitle>
                <CardDescription>Average ratings across all check-ins (1–3 scale)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(DOMAIN_LABELS).map(([key, label]) => {
                    const val = Number(insights.domainAverages[key]) || 0;
                    const pct = Math.round((val / 3) * 100);
                    return (
                      <div key={key} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">{label}</p>
                        <p className="text-xl font-bold text-forest-700">{val.toFixed(1)}</p>
                        <div className="mt-1.5 h-1.5 bg-gray-200 rounded-full">
                          <div className="h-1.5 bg-forest-400 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* School & grade breakdown */}
          {insights.classBreakdown.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <School className="w-4 h-4" /> School & Grade Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {insights.classBreakdown.map((row, i) => (
                    <div key={i} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div>
                        <span className="font-medium text-gray-800">{row.school_name || "Unknown School"}</span>
                        <span className="text-gray-400 mx-2">·</span>
                        <span className="text-gray-500 text-sm">{row.grade_level || "Unknown Grade"}</span>
                      </div>
                      <Badge variant="secondary">
                        {row.class_count} {Number(row.class_count) === 1 ? "class" : "classes"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Domain trends over time */}
          {insights.domainTrends.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="w-4 h-4" /> Domain Ratings Over Time
                </CardTitle>
                <CardDescription>Monthly averages across all educators</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 pr-4 text-gray-500 font-medium">Month</th>
                        {Object.values(DOMAIN_LABELS).map(label => (
                          <th key={label} className="text-center py-2 px-2 text-gray-500 font-medium">{label}</th>
                        ))}
                        <th className="text-center py-2 px-2 text-gray-500 font-medium">Check-ins</th>
                      </tr>
                    </thead>
                    <tbody>
                      {insights.domainTrends.map((row, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 pr-4 text-gray-700 font-medium">
                            {new Date(row.month).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                          </td>
                          {Object.keys(DOMAIN_LABELS).map(k => (
                            <td key={k} className="py-2 px-2 text-center text-forest-700 font-medium">
                              {Number(row[k]).toFixed(1)}
                            </td>
                          ))}
                          <td className="py-2 px-2 text-center text-gray-500">{row.checkin_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </motion.div>
  );
}
